// Friendly error mapper for the studio.
//
// Goal: every place in the UI that renders `(err as Error).message` should
// instead render `friendly_error(err)`. This turns leaky backend payloads
// like "MCP http://localhost:7100/rpc tools/call -> [-32603] slot 2 already
// taken in chit 019e55df-...." into operator-friendly text like
// "Position 2 is already filled."
//
// Two layers cooperate to keep messages clean:
//   1. Backend strips the MCP wrapper + UUIDs at the mcp_call boundary
//      (packages/backend/src/mcp.ts).
//   2. This helper maps known patterns to localised-friendly strings, and
//      falls back to the underlying message otherwise.
//
// Structured errors (e.g. {kind: 'SLOT_TAKEN', ...}) should be detected via
// the typed guards exported here (e.g. `is_slot_taken_error`) BEFORE falling
// back to `friendly_error()` -- the caller can then render a custom UI
// (confirm-replace prompt, etc.) rather than a generic toast.

/* eslint-disable @typescript-eslint/no-explicit-any */

interface ErrorLike {
  code?: string;
  message?: string;
  data?: Record<string, unknown>;
  status?: number;
}

function as_error_like(err: unknown): ErrorLike {
  if (err && typeof err === 'object') return err as ErrorLike;
  return {};
}

/** Structured payload thrown by `chits.add_subscriber_to_cycle` when the
 *  requested slot is already filled by a different subscriber. */
export interface SlotTakenData {
  kind: 'SLOT_TAKEN';
  cycle_index: number;
  current_recipient_id: string;
  current_recipient_name: string;
  current_membership_id: string;
}

/** Typed guard for the SLOT_TAKEN structured error. Returns the payload
 *  when matched, or `null` otherwise. */
export function is_slot_taken_error(err: unknown): SlotTakenData | null {
  const e = as_error_like(err);
  const data = e.data;
  if (
    e.code === 'CONFLICT' &&
    data &&
    typeof data === 'object' &&
    (data as any).kind === 'SLOT_TAKEN' &&
    typeof (data as any).cycle_index === 'number' &&
    typeof (data as any).current_recipient_name === 'string' &&
    typeof (data as any).current_membership_id === 'string'
  ) {
    return data as unknown as SlotTakenData;
  }
  return null;
}

interface Pattern {
  match: RegExp;
  replace: (m: RegExpMatchArray) => string;
}

const PATTERNS: Pattern[] = [
  {
    match: /^slot\s+(\d+)\s+already taken/i,
    replace: (m) => `Position ${m[1]} is already filled.`,
  },
  {
    match: /^slot\s+(\d+)\s+out of range/i,
    replace: (m) => `Position ${m[1]} isn't valid for this chit.`,
  },
  {
    match: /^subscriber\s+already in chit at slot\s+(\d+)/i,
    replace: (m) => `This subscriber is already at position ${m[1]} in this chit.`,
  },
  {
    match: /^subscriber\s+already in chit/i,
    replace: () => 'This subscriber is already in this chit.',
  },
  {
    match: /^chit not found/i,
    replace: () => 'This chit no longer exists. Please reload the page.',
  },
  {
    match: /^subscriber not found/i,
    replace: () => "That subscriber doesn't exist anymore.",
  },
  {
    match: /^replacement subscriber not found/i,
    replace: () => "The replacement subscriber doesn't exist.",
  },
  {
    match: /^membership not found/i,
    replace: () => 'That membership no longer exists. Please reload the page.',
  },
  {
    match: /^cycle\s+(\d+)\s+(?:is\s+)?locked/i,
    replace: (m) => `Cycle ${m[1]} has already been paid and can't be edited.`,
  },
  {
    match: /\blocked cycle\b/i,
    replace: () => "This cycle has already been paid and can't be edited.",
  },
  {
    match: /^recipient already assigned/i,
    replace: () => 'This person already receives a payout on another cycle.',
  },
  {
    match: /^phone\s+(?:already\s+)?(?:exists|in use)/i,
    replace: () => 'A subscriber with this phone number already exists.',
  },
  {
    match: /^failed to fetch|networkerror|network request failed/i,
    replace: () => "Couldn't reach the server. Check your connection and try again.",
  },
];

/** Map a thrown error / network failure / oRPC error into a user-facing
 *  string. Never returns an MCP wrapper prefix, internal UUIDs, or a
 *  Mongo/Zod-internal stack trace. */
export function friendly_error(err: unknown): string {
  if (err == null) return 'Something went wrong.';

  const e = as_error_like(err);
  const raw = (e.message ?? String(err)).trim();

  if (typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn('[friendly_error]', err);
  }

  if (e.code === 'UNAUTHORIZED') return "You aren't signed in. Please sign in again.";
  if (e.code === 'FORBIDDEN') return "You don't have permission to do this. Ask an admin.";
  if (e.code === 'NOT_FOUND') return raw || "We couldn't find what you were looking for.";
  if (e.code === 'TOO_MANY_REQUESTS') return 'Too many requests. Please wait a moment and try again.';
  if (e.code === 'BAD_GATEWAY' || e.code === 'SERVICE_UNAVAILABLE' || e.code === 'GATEWAY_TIMEOUT') {
    return 'The server is temporarily unavailable. Please try again in a moment.';
  }
  if (e.code === 'INTERNAL_SERVER_ERROR' && (!raw || raw === 'Internal server error')) {
    return 'Something went wrong on the server. Please try again.';
  }

  for (const p of PATTERNS) {
    const m = raw.match(p.match);
    if (m) return p.replace(m);
  }

  if (typeof e.status === 'number' && e.status >= 500) {
    return 'Something went wrong on the server. Please try again in a moment.';
  }

  return raw || 'Something went wrong.';
}
